import React, { useState } from 'react';
import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/16/solid';

/**
 * Linear's grouped list, generalised (#603 follow-on 3): groups under a
 * header with a count, one dense line per item, collapsible groups. The
 * caller decides the grouping and draws the row; this draws the rest.
 *
 * Optional selection puts a checkbox before each row and one in each group
 * header — for the pages whose bulk actions (message selected, remove
 * selected) worked off the old table's checkboxes. Rows stay buttons, so
 * the list is keyboard-operable either way.
 */
export interface ListGroup<T> {
  key: string;
  label: string;
  icon?: React.ReactNode;
  items: T[];
}

interface Props<T> {
  groups: ListGroup<T>[];
  getKey: (item: T) => string;
  renderRow: (item: T) => React.ReactNode;
  onOpen: (item: T) => void;
  /** Accessible name for a row, e.g. the address or the member's name. */
  rowLabel: (item: T) => string;
  selection?: {
    selected: Set<string>;
    onChange: (next: Set<string>) => void;
  };
  emptyMessage: string;
}

const checkboxClass =
  'h-3.5 w-3.5 shrink-0 rounded border-line-strong bg-transparent text-accent focus:ring-accent focus:ring-offset-0';

export function GroupedList<T>({
  groups,
  getKey,
  renderRow,
  onOpen,
  rowLabel,
  selection,
  emptyMessage,
}: Props<T>) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  if (groups.length === 0) {
    return <p className="px-4 py-10 text-center text-[13px] text-fg-muted">{emptyMessage}</p>;
  }

  const toggle = (keys: string[], on: boolean) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
    selection.onChange(next);
  };

  return (
    <div>
      {groups.map((g) => {
        const isCollapsed = !!collapsed[g.key];
        const keys = g.items.map(getKey);
        const allSelected = !!selection && keys.every((k) => selection.selected.has(k));
        const headingId = `list-group-${g.key}`;
        return (
          <section key={g.key} aria-labelledby={headingId}>
            <div className="flex items-center gap-2 bg-fg/[0.025] px-4 py-1.5">
              {selection && (
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={allSelected}
                  onChange={(e) => toggle(keys, e.target.checked)}
                  aria-label={`Select all ${g.label}`}
                />
              )}
              <h3 id={headingId} className="flex-1">
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !isCollapsed }))}
                  aria-expanded={!isCollapsed}
                  className="flex w-full items-center gap-2 text-left text-[13px] font-medium text-fg-secondary"
                >
                  <ChevronDownIcon
                    className={clsx('h-3.5 w-3.5 text-fg-faint transition-transform', isCollapsed && '-rotate-90')}
                    aria-hidden
                  />
                  {g.icon}
                  {g.label}
                  <span className="font-normal text-fg-muted">{g.items.length}</span>
                </button>
              </h3>
            </div>
            {!isCollapsed && (
              <ul className="divide-y divide-line/50">
                {g.items.map((item) => {
                  const key = getKey(item);
                  return (
                    <li key={key} className="flex items-center gap-3 px-4 hover:bg-fg/[0.03]">
                      {selection && (
                        <input
                          type="checkbox"
                          className={checkboxClass}
                          checked={selection.selected.has(key)}
                          onChange={(e) => toggle([key], e.target.checked)}
                          aria-label={`Select ${rowLabel(item)}`}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => onOpen(item)}
                        aria-label={rowLabel(item)}
                        className="flex min-w-0 flex-1 items-center gap-3 py-2 text-left text-[13px] focus:outline-none focus-visible:bg-fg/5"
                      >
                        {renderRow(item)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
